import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import type {
  DataMapFlowOperationalSummary,
  DataMapExecutiveSummary,
  InformationSystemExecutiveSummary,
} from '../../services/enterpriseOperationalUxApi'
import type { DataMapRow } from '../assets/dataMapRowParts'
import { getStandardDisplayName } from '../../utils/complianceStandard'
import { formatRelativeTime } from '../../utils/operationalUx'
import {
  mapFlowToUnifiedContext,
  mapInformationSystemToUnifiedContext,
} from '../../utils/unifiedExecutiveContextMappers'
import ExecutiveOperationalContextPanel from './context/ExecutiveOperationalContextPanel'
import OperationalRuntimeTimeline from './context/OperationalRuntimeTimeline'
import OperationalBlastRadiusPanel from './context/OperationalBlastRadiusPanel'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import styles from '../../styles/components/assets/AssetsDataMap.module.scss'

export type DataMapSelection =
  | { kind: 'system'; systemId: string; row?: DataMapRow }
  | { kind: 'flow'; flow: DataMapFlowOperationalSummary }
  | { kind: 'event'; event: DataMapExecutiveSummary['topologyEvents'][number] }
  | { kind: 'topologyIssue'; impact: DataMapExecutiveSummary['blastRadiusImpacts'][number] }
  | null

type Props = {
  selection: DataMapSelection
  executiveBySystemId: Record<string, InformationSystemExecutiveSummary>
  onClose?: () => void
}

function buildRecommendations(
  exec: InformationSystemExecutiveSummary | null,
  flow: DataMapFlowOperationalSummary | null,
): string[] {
  const actions: string[] = []
  if (exec?.recommendedNextActions?.length) return exec.recommendedNextActions
  if (flow?.operationalState.staleEvidence) actions.push('Review stale evidence on connected systems')
  if (flow?.operationalState.degraded) actions.push('Remediate degraded controls impacting this flow')
  if (flow?.blastRadius.blastRadiusContribution > 0) actions.push('Assess shared control blast radius')
  return actions.slice(0, 4)
}

export default function DataMapExecutiveContextPanel({
  selection,
  executiveBySystemId,
  onClose,
}: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()

  if (!selection) {
    return (
      <div className={styles.contextPanelEmpty}>
        <p>{t('enterpriseOperationalUx.dataMap.selectTopology', 'Select a system, flow, runtime event, or topology issue.')}</p>
      </div>
    )
  }

  if (selection.kind === 'flow') {
    const flow = selection.flow
    const sourceExec = executiveBySystemId[flow.sourceSystemId]
    const targetExec = executiveBySystemId[flow.targetSystemId]
    const recommendations = buildRecommendations(sourceExec || targetExec || null, flow)
    const flowContext = mapFlowToUnifiedContext(flow)
    flowContext.recommendations = recommendations

    return (
      <div className={styles.contextPanel}>
        <header className={styles.contextPanelHeader}>
          <h3>{t('enterpriseOperationalUx.dataMap.operationalTopology', 'Operational topology')}</h3>
          {onClose ? (
            <button type="button" className={styles.contextClose} onClick={onClose}>
              {t('common.close', 'Close')}
            </button>
          ) : null}
        </header>
        <ExecutiveOperationalContextPanel
          context={flowContext}
          mode="compact"
          sections={{ stability: false, metrics: false, workflows: false }}
        />
        <dl className={entStyles.metricList}>
          <div className={entStyles.metricRow}>
            <dt>{t('enterpriseOperationalUx.dataMap.topologyPressure', 'Topology pressure')}</dt>
            <dd>
              {flow.operationalState.sourcePressure || '—'} / {flow.operationalState.targetPressure || '—'}
            </dd>
          </div>
          <div className={entStyles.metricRow}>
            <dt>{t('enterpriseOperationalUx.dataMap.blastRadiusContribution', 'Blast radius contribution')}</dt>
            <dd>{flow.blastRadius.blastRadiusContribution}</dd>
          </div>
          <div className={entStyles.metricRow}>
            <dt>{t('enterpriseOperationalUx.impactedStandards', 'Impacted standards')}</dt>
            <dd>
              {flow.blastRadius.impactedStandards.length
                ? flow.blastRadius.impactedStandards.map((c) => getStandardDisplayName(t, c)).join(', ')
                : '—'}
            </dd>
          </div>
          <div className={entStyles.metricRow}>
            <dt>{t('enterpriseOperationalUx.dataMap.operationalPropagation', 'Operational propagation')}</dt>
            <dd>{flow.runtime.driftDetected ? t('enterpriseOperationalUx.dataMap.yes', 'Yes') : '—'}</dd>
          </div>
        </dl>

        {flow.blastRadius.blastHits.length ? (
          <div style={{ marginTop: 12 }}>
            {flow.blastRadius.blastHits.map((hit) => (
              <div key={hit.controlCode} className={entStyles.blastItem}>
                <strong>{hit.controlCode}</strong> — {hit.summary}
              </div>
            ))}
          </div>
        ) : null}

        {flow.workflowStages.length ? (
          <div className={styles.topologyWorkflowRow} style={{ marginTop: 12 }}>
            {flow.workflowStages.map((w) => (
              <button
                key={w.standardCode}
                type="button"
                className={styles.workflowChip}
                onClick={() => navigate(`/compliance-workflow/${encodeURIComponent(w.standardCode)}`)}
              >
                {getStandardDisplayName(t, w.standardCode)} — {w.workflowStage}
              </button>
            ))}
          </div>
        ) : null}

        {recommendations.length ? (
          <ul className={styles.recommendList}>
            {recommendations.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        ) : null}
      </div>
    )
  }

  if (selection.kind === 'event') {
    const ev = selection.event
    return (
      <div className={styles.contextPanel}>
        <header className={styles.contextPanelHeader}>
          <h3>{t('enterpriseOperationalUx.dataMap.runtimePropagation', 'Runtime propagation')}</h3>
          {onClose ? (
            <button type="button" className={styles.contextClose} onClick={onClose}>
              {t('common.close', 'Close')}
            </button>
          ) : null}
        </header>
        <OperationalRuntimeTimeline
          events={[
            {
              id: ev.id,
              at: ev.at,
              eventType: ev.eventType,
              severity: ev.severity,
              summary: ev.summary,
              relatedInformationSystemId: ev.relatedInformationSystemId,
              relatedStandardCode: ev.relatedStandardCode,
            },
          ]}
          mode="full"
        />
        <p className={styles.contextMeta}>{formatRelativeTime(ev.at)}</p>
      </div>
    )
  }

  if (selection.kind === 'topologyIssue') {
    const impact = selection.impact
    return (
      <div className={styles.contextPanel}>
        <header className={styles.contextPanelHeader}>
          <h3>{t('enterpriseOperationalUx.dataMap.topologyIssue', 'Topology issue')}</h3>
          {onClose ? (
            <button type="button" className={styles.contextClose} onClick={onClose}>
              {t('common.close', 'Close')}
            </button>
          ) : null}
        </header>
        <OperationalBlastRadiusPanel
          hits={[
            {
              controlCode: impact.controlCode,
              summary: impact.summary,
              impactedStandards: impact.impactedStandards || [],
              standardCount: impact.standardCount,
              systemCount: impact.systemCount,
              severity: 'MODERATE',
            },
          ]}
          mode="expanded"
        />
      </div>
    )
  }

  const exec = executiveBySystemId[selection.systemId]
  const name = selection.row?.informationSystemName || selection.systemId

  if (!exec) {
    return (
      <div className={styles.contextPanel}>
        <header className={styles.contextPanelHeader}>
          <h3>{name}</h3>
        </header>
        <p className={entStyles.emptyState}>{t('enterpriseOperationalUx.noExecutiveBaseline', 'No executive baseline established yet.')}</p>
      </div>
    )
  }

  const context = mapInformationSystemToUnifiedContext(exec, name)

  return (
    <div className={styles.contextPanel}>
      <ExecutiveOperationalContextPanel context={context} title={name} mode="compact" onClose={onClose} />
    </div>
  )
}
